// shadcn/ui Table (new-york), vendored with logical properties only. The
// wrapper gives every table its own horizontal scroll (§4.3.4: wide content
// scrolls inside its container, the page never scrolls sideways).
//
// NFR-06 — "every column must grow rather than clip": every table in the
// product is auto-layout (the fixed-layout utility appears nowhere in the
// tree — not spelled out here, because the guard scans for the literal), so a
// `w-*` hint on a header is a preference the layout algorithm overrides when
// a longer translation needs more; `whitespace-nowrap` on TableHead/TableCell
// makes content the column's minimum width, and the grown table scrolls in
// this wrapper instead of clipping. Held by `nfr06-grow-not-clip.test.tsx`.
import * as React from 'react';

import { cn } from '@/lib/utils';

function Table({ className, ...props }: React.ComponentProps<'table'>) {
  return (
    <div data-slot="table-container" className="relative w-full overflow-x-auto">
      <table
        data-slot="table"
        className={cn('w-full caption-bottom text-sm', className)}
        {...props}
      />
    </div>
  );
}

function TableHeader({ className, ...props }: React.ComponentProps<'thead'>) {
  return <thead data-slot="table-header" className={cn('[&_tr]:border-b', className)} {...props} />;
}

function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
  return (
    <tbody data-slot="table-body" className={cn('[&_tr:last-child]:border-0', className)} {...props} />
  );
}

function TableFooter({ className, ...props }: React.ComponentProps<'tfoot'>) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn('border-t bg-muted/50 font-medium [&>tr]:last:border-b-0', className)}
      {...props}
    />
  );
}

function TableRow({ className, ...props }: React.ComponentProps<'tr'>) {
  return (
    <tr
      data-slot="table-row"
      className={cn('border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted', className)}
      {...props}
    />
  );
}

function TableHead({ className, ...props }: React.ComponentProps<'th'>) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        'h-10 whitespace-nowrap px-2 text-start align-middle font-medium text-muted-foreground',
        className,
      )}
      {...props}
    />
  );
}

/**
 * ADM-08 §5 field 5 / BR-ADM-08-07 — density "changes ONE THING: the top and
 * bottom padding inside every data-table cell — 9, 14, or 19 pixels. It does
 * not change card padding, form controls, or the menu."
 *
 * So the vertical padding comes from `--cell-padding-y` (set on <html> by
 * `lib/personalization.ts`) and the horizontal padding does not move.
 *
 * BR-ADM-08-08 — "EVERY table in the system must obey the density setting.
 * Known offenders are listed in EC-19-47" — is satisfied by CONVERSION, not by
 * this component existing. That distinction cost the build a wave.
 *
 * This comment once claimed "there is one TableCell in this product and every
 * table is built from it, so the rule is a property of this component rather
 * than a checklist. EC-19-47's list of known offenders has no members here."
 * The RULE COVERAGE audit counted 173 raw `<td>` elements across 18 feature
 * files, each with its own fixed `p-2`/`py-1.5` padding and no
 * `--cell-padding-y` anywhere near it — and `dashboards/cashflow-page.tsx` was
 * one of them, which is to say the edge case the comment declared closed was
 * open at "the cash-flow tables", the FIRST offender EC-19-47 names.
 *
 * Fifteen of the eighteen are converted. The remaining three are argued for in
 * `DENSITY_EXEMPT_TABLES` below and pinned by
 * `src/test/table-density.test.tsx`, which is what turns EC-19-47 — written as
 * a CLASS of defect, which is why nobody could ever check it — into a closed
 * set. Do not add to that list without adding the sentence that justifies it.
 */
function TableCell({ className, style, ...props }: React.ComponentProps<'td'>) {
  return (
    <td
      data-slot="table-cell"
      className={cn('whitespace-nowrap px-2 align-middle', className)}
      style={{
        paddingTop: 'var(--cell-padding-y)',
        paddingBottom: 'var(--cell-padding-y)',
        ...style,
      }}
      {...props}
    />
  );
}

/**
 * THE CLOSED SET — every raw `<td>` left under `src/features/`, and why it is
 * not a data-table cell.
 *
 * `EC-19-47` describes a CLASS ("every data table in the system must use the
 * shared row-padding setting") and names three examples rather than members.
 * A class cannot be checked, which is exactly how 173 offenders survived a
 * comment claiming the rule was closed. This list turns it into a membership
 * question, the way `UNPAGED_CALLERS` did for `NFR-42`: `table-density.test.tsx`
 * greps the feature tree for `<td` and fails on any file that is not here.
 *
 * IT MAY SHRINK AND MAY NOT GROW. Removing an entry means a table joined the
 * setting; adding one means a new raw cell, which has to be argued in writing
 * first — and the argument has to be about BR-ADM-08-07, whose scope is
 * "every DATA-TABLE cell … and NOTHING ELSE", not about convenience.
 */
export const DENSITY_EXEMPT_TABLES: readonly { file: string; why: string }[] = [
  {
    file: 'features/admin/role-grid.tsx',
    why: 'ADM-06 §5 is a 12×5 permission MATRIX, not a list of records: every cell in it is a checkbox, and ADM-08 §5 field 5 puts form controls outside density by name ("it does not change card padding, FORM CONTROLS, or the menu"). Its row height is the checkbox, not the padding, and BR-ADM-08-07 scopes the setting to data-table cells "and nothing else" — so honouring it here would break that rule rather than BR-ADM-08-08.',
  },
  {
    file: 'features/hr/attendance-page.tsx',
    why: 'HR-03: "each person has ONE SQUARE PER DAY of the month, coloured and lettered by what happened". The register is a fixed-geometry calendar of 31 size-6 buttons per employee, whose height is the square; padding it by 19px would treble a row and push the month off the screen. Its real data tables on the same screen already use TableCell — the raw cells are the square grid alone.',
  },
  {
    file: 'features/hr/leave-page.tsx',
    why: 'HR-04 calendar tab: the same one-square-per-day month grid as the attendance register, drawn as size-5 colour swatches so coverage gaps are visible at a glance. A swatch is a picture of a day, not a value in a column, and every other table on the screen is already a TableCell table.',
  },
] as const;

export { Table, TableHeader, TableBody, TableFooter, TableRow, TableHead, TableCell };
